import { WEAPONS, getWeapon, type WeaponSpec, type WeaponClass } from "@hupper/shared/weapons";
import type { GameClient } from "../net/GameClient";
import { weaponIconSvg, grenadeIconSvg, GRENADE_ORDER } from "./weaponIcons";

/**
 * WeaponInventoryHud — CS:GO-style bottom-right slot list.
 *
 *   ┌─────────────────────────┐
 *   │   AK-47   [rifle svg] 1  │  ← primary  (only shown when owned)
 *   │           [pistol]   2  │  ← secondary
 *   │           [knife]    3  │  ← melee (always)
 *   │            ● ◐ ◑        │  ← utility row: a grenade icon per owned
 *   └─────────────────────────┘     type, or dim dots when you carry none.
 *
 * Only the equipped slot is bright + shows its name; the rest are dimmed
 * icon+number, matching the reference. The bomb/C4 slot is intentionally
 * omitted (not in play yet).
 *
 * Visual-only — driven entirely off the server snapshot (`ownedWeapons`,
 * `weaponId`). Re-renders on equip / inventory change, not every frame.
 */
export class WeaponInventoryHud {
  private readonly client: GameClient;
  private readonly root: HTMLDivElement;
  private readonly unsubs: Array<() => void> = [];
  /** Signature of what's actually drawn (equipped id + owned set). The
   *  onPlayerChange feed fires ~32 Hz on snapshot churn (position / ammo /
   *  lastProcessedInputSeq); rebuilding innerHTML each time re-creates the
   *  icon <img> tags, which visibly blink on iPad Safari. Skip unless the
   *  rendered content really changed. */
  private lastSig = "";

  constructor(client: GameClient, onSlotTap?: (slot: 1 | 2 | 3 | 4) => void) {
    this.client = client;
    this.root = document.createElement("div");
    this.root.id = "weapon-inv";
    document.body.appendChild(this.root);
    this.injectStyles();

    // Touch mode: the slot rows double as tap targets (1/2/3/4 keys have
    // no keyboard on a phone). Delegated so it survives re-renders.
    if (onSlotTap) {
      this.root.classList.add("tappable");
      this.root.addEventListener("click", (e) => {
        const row = (e.target as HTMLElement).closest("[data-slot]");
        if (!row) return;
        const slot = Number(row.getAttribute("data-slot"));
        if (slot === 1 || slot === 2 || slot === 3 || slot === 4) onSlotTap(slot);
      });
    }

    const refresh = () => this.render();
    refresh();
    const onSelf = (_: unknown, sid: string) => {
      if (sid === this.client.sessionId) refresh();
    };
    this.unsubs.push(this.client.onPlayerAdd(onSelf));
    this.unsubs.push(this.client.onPlayerChange(onSelf));
    this.unsubs.push(this.client.onWeaponEquip((msg) => {
      if (msg.sessionId === this.client.sessionId) refresh();
    }));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  private render(): void {
    const self = this.client.getLatestSelf();
    const owned = new Set(self?.ownedWeapons ?? ["knife", "g18"]);
    owned.add("knife"); // melee is always carried
    const equipped = self?.weaponId ?? "g18";

    // Snapshot churn guard — only rebuild the DOM when the drawn content
    // (equipped weapon or inventory) actually changed.
    const sig = `${equipped}|${[...owned].sort().join(",")}`;
    if (sig === this.lastSig) return;
    this.lastSig = sig;

    // Weapon rows, top → bottom: primary (1), secondary (2), melee (3).
    const rows: Array<{ spec: WeaponSpec; slot: number }> = [];
    const primary = pickOwned(owned, ["rifle", "smg", "sniper"], equipped);
    const secondary = pickOwned(owned, ["pistol"], equipped);
    if (primary) rows.push({ spec: primary, slot: 1 });
    if (secondary) rows.push({ spec: secondary, slot: 2 });
    rows.push({ spec: getWeapon("knife"), slot: 3 });

    const weaponsHtml = rows
      .map(({ spec, slot }) => {
        const isEq = spec.id === equipped;
        const name = isEq ? `<div class="wi-name">${escapeHtml(spec.name)}</div>` : "";
        return `<div class="wi-row${isEq ? " equipped" : ""}" data-slot="${slot}">
          <div class="wi-weapon-info">
            <div class="wi-icon-row">
              <span class="wi-icon">${weaponIconSvg(spec)}</span>
              <span class="wi-slot">${slot}</span>
            </div>
            ${name}
          </div>
        </div>`;
      })
      .join("");

    // Utility row — owned grenade icons (or placeholder dots when empty),
    // tagged with slot number 4 to match the weapon rows (1 / 2 / 3).
    const nades = GRENADE_ORDER.filter((id) => owned.has(id));
    const nadeEquipped = nades.some((id) => id === equipped);
    const nadesHtml = nades.length
      ? nades
          .map((id) => {
            const isEq = id === equipped;
            return `<span class="wi-nade${isEq ? " equipped" : ""}">${grenadeIconSvg(id)}</span>`;
          })
          .join("")
      : `<span class="wi-dot"></span><span class="wi-dot"></span><span class="wi-dot"></span><span class="wi-dot"></span>`;

    this.root.innerHTML = `
      <div class="wi-weapons">${weaponsHtml}</div>
      <div class="wi-utility${nades.length ? "" : " empty"}${nadeEquipped ? " equipped" : ""}" data-slot="4">
        <div class="wi-nades">${nadesHtml}</div>
        <span class="wi-slot">4</span>
      </div>
    `;
  }

  private injectStyles(): void {
    if (document.getElementById("weapon-inv-styles")) return;
    const style = document.createElement("style");
    style.id = "weapon-inv-styles";
    style.textContent = `
      #weapon-inv {
        position: fixed;
        right: 26px;
        bottom: 30px;
        z-index: 8;
        pointer-events: none;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 12px;
        font-family: 'Geist', 'Trebuchet MS', sans-serif;
      }

      /* ── Weapon slots ── */
      #weapon-inv .wi-weapons {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 12px;
      }
      #weapon-inv .wi-row {
        display: flex;
        justify-content: flex-end;
        color: rgba(245, 239, 230, 0.4);
        transition: color 160ms ease;
      }
      #weapon-inv .wi-row.equipped { color: #f5efe6; }
      #weapon-inv .wi-weapon-info {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 2px;
      }
      #weapon-inv .wi-icon-row {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      #weapon-inv .wi-name {
        font-size: 13px;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: rgba(245, 239, 230, 0.8);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.75);
        padding-right: 26px; /* Align with the icon, ignoring the slot number width */
      }
      #weapon-inv .wi-icon { display: inline-flex; align-items: center; }
      /* Height-based so every weapon keeps its real aspect ratio (a pistol
         is stubby, a rifle long, the AWP very long) — never stretched. */
      #weapon-inv .wi-icon .hud-wic {
        height: 25px;
        width: auto;
        max-width: 82px;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
      }
      #weapon-inv .wi-row.equipped .wi-icon .hud-wic {
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.85));
      }
      /* Rendered PNG variant — dims via opacity (no currentColor tint), and
         object-fit keeps the longest guns letterboxed instead of stretched. */
      #weapon-inv .wi-icon .hud-wic-img {
        height: 26px;
        width: auto;
        max-width: 100px;
        object-fit: contain;
        opacity: 0.42;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
        transition: opacity 160ms ease;
      }
      #weapon-inv .wi-row.equipped .wi-icon .hud-wic-img {
        opacity: 1;
        filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.85));
      }
      #weapon-inv .wi-slot {
        min-width: 16px;
        padding: 1px 4px;
        text-align: center;
        font-size: 11px;
        font-weight: 700;
        line-height: 1.3;
        border-radius: 3px;
        color: rgba(245, 239, 230, 0.45);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.7);
        font-variant-numeric: tabular-nums;
      }
      #weapon-inv .wi-row.equipped .wi-slot {
        color: rgba(245, 239, 230, 0.7);
      }

      /* ── Utility row ── */
      #weapon-inv .wi-utility {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 10px; /* matches the weapon icon→slot gap so "4" lines up under 1/2/3 */
        min-height: 22px;
      }
      #weapon-inv .wi-nades {
        display: flex;
        align-items: center;
        gap: 9px;
      }
      #weapon-inv .wi-utility.equipped .wi-slot {
        color: rgba(245, 239, 230, 0.7);
      }
      #weapon-inv .wi-nade {
        display: inline-flex;
        opacity: 0.85;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
        transition: opacity 160ms ease, transform 160ms ease;
      }
      #weapon-inv .wi-nade .hud-nic { width: 22px; height: 22px; }
      #weapon-inv .wi-nade .hud-nic-img { width: 22px; height: 22px; object-fit: contain; }
      #weapon-inv .wi-nade.equipped { opacity: 1; transform: translateY(-2px); }
      #weapon-inv .wi-nade.equipped .hud-nic,
      #weapon-inv .wi-nade.equipped .hud-nic-img {
        filter: drop-shadow(0 0 5px rgba(232, 177, 78, 0.85));
      }
      /* Touch mode — the rows are tap targets for slot switching. The
         root stays pointer-events:none so only the rows catch taps. */
      #weapon-inv.tappable .wi-row,
      #weapon-inv.tappable .wi-utility {
        pointer-events: auto;
        cursor: pointer;
      }
      #weapon-inv .wi-dot {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: rgba(245, 239, 230, 0.3);
        box-shadow: 0 1px 1px rgba(0, 0, 0, 0.5);
      }
    `;
    document.head.appendChild(style);
  }
}

/**
 * Pick the player's owned weapon for a slot. Prefers the equipped one (so the
 * active slot is what's shown), else the first owned of that class group.
 */
function pickOwned(owned: Set<string>, classes: WeaponClass[], equipped: string): WeaponSpec | null {
  const candidates = WEAPONS.filter((w) => owned.has(w.id) && classes.includes(w.class));
  if (candidates.length === 0) return null;
  return candidates.find((w) => w.id === equipped) ?? candidates[0];
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
